import type { TFunction } from "i18next"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Check, Cone, Crosshair, Eye, PenTool, SkipBack, Spline } from "lucide-react"
import { ConfirmActionDialog } from "@/components/confirm-action-dialog"
import { Button } from "@/components/ui/primitives"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { MENU_Z_INDEX, useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { EntityThumbSquare } from "@/components/workspace/entity-thumb-square"
import { useTranslation } from "@/hooks/use-translation"
import { SCENE_3D_TOOLBAR_MENU_ATTR } from "@/lib/dialog-layer"
import { type PathEditUi, type PathLook } from "@/lib/location-scene-3d/camera-path"
import { cn } from "@/lib/utils"

type LocationScenePathEditPanelProps = {
  ui: PathEditUi
  targetLabel: string | null
  targetThumb: string | null
  targetPrimitive: boolean
  hasPath: boolean
  playing: boolean
  onDraw: (draw: boolean) => void
  onLook: (look: PathLook) => void
  onPickObject: () => void
  onArmLookRewrite: () => void
  onCancelLook: () => void
  onGoToPathStart: () => void
}

const MENU_ITEM =
  "flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-ui-text-primary outline-none hover:bg-ui-surface-sunken disabled:pointer-events-none disabled:opacity-40"

const LOCKED = "disabled:pointer-events-none disabled:opacity-40"

const DIVIDER = <span className="mx-0.5 h-4 w-px shrink-0 bg-ui-border" aria-hidden />

export function LocationScenePathEditPanel({
  ui,
  targetLabel,
  targetThumb,
  targetPrimitive,
  hasPath,
  playing,
  onDraw,
  onLook,
  onPickObject,
  onArmLookRewrite,
  onCancelLook,
  onGoToPathStart,
}: LocationScenePathEditPanelProps) {
  const { t } = useTranslation(["workspace"])
  const [menuOpen, setMenuOpen] = useState(false)
  const [replaceOpen, setReplaceOpen] = useState(false)
  const menuRootRef = useRef<HTMLDivElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const observeUid = ui.look.mode === "observe" ? ui.look.object_uid : ""

  const { menuStyle, portalRoot } = useDropdownMenu({
    open: menuOpen,
    direction: "down",
    align: "left",
    anchorRef: menuButtonRef,
    menuRef,
    rootRef: menuRootRef,
    resolveWidth: () => ({ width: "max-content", maxWidth: "min(20rem, calc(100vw - 16px))" }),
    onOutsideClick: () => setMenuOpen(false),
    estimatedMenuHeightPx: 280,
    estimatedMenuWidthPx: 280,
    portalToBody: false,
    zIndex: MENU_Z_INDEX,
  })

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      event.stopPropagation()
      setMenuOpen(false)
    }
    document.addEventListener("keydown", onKey, true)
    return () => document.removeEventListener("keydown", onKey, true)
  }, [menuOpen])

  useEffect(() => {
    if (playing && !ui.lookRecording) setMenuOpen(false)
  }, [playing, ui.lookRecording])

  const requestLook = () => {
    setMenuOpen(false)
    if (!hasPath) return
    if (ui.lookRecorded) {
      setReplaceOpen(true)
      return
    }
    onArmLookRewrite()
  }

  const modeIcon =
    ui.look.mode === "observe" ? (
      <Crosshair className="h-3.5 w-3.5 shrink-0" />
    ) : ui.look.mode === "recorded" ? (
      <Eye className="h-3.5 w-3.5 shrink-0" />
    ) : (
      <Spline className="h-3.5 w-3.5 shrink-0" />
    )

  const modeDetail = (() => {
    if (ui.look.mode === "observe") {
      return targetLabel
        ? { text: targetLabel, accent: false }
        : { text: t("locationsPage.scene3d.trajectory.pickTarget"), accent: true }
    }
    if (ui.look.mode === "recorded") {
      if (ui.lookRecorded) return null
      return { text: t("locationsPage.scene3d.trajectory.noLook"), accent: false }
    }
    return null
  })()

  const menu =
    menuOpen && menuStyle && portalRoot
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={menuStyle}
            className="overflow-hidden rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-[var(--ui-shadow-md)]"
            {...{ [SCENE_3D_TOOLBAR_MENU_ATTR]: "" }}
          >
            <button
              type="button"
              role="menuitem"
              className={MENU_ITEM}
              onClick={() => {
                setMenuOpen(false)
                onLook({ mode: "tangent" })
              }}
            >
              <Spline className="h-3.5 w-3.5 shrink-0" />
              {t("locationsPage.scene3d.trajectory.lookAlong")}
              {ui.look.mode === "tangent" ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
            </button>
            <button
              type="button"
              role="menuitem"
              className={MENU_ITEM}
              onClick={() => {
                setMenuOpen(false)
                onLook({ mode: "recorded" })
              }}
            >
              <Eye className="h-3.5 w-3.5 shrink-0" />
              {t("locationsPage.scene3d.trajectory.lookRecorded")}
              {ui.look.mode === "recorded" ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
            </button>
            <button
              type="button"
              role="menuitem"
              className={cn(MENU_ITEM, "pl-8 text-ui-text-secondary")}
              disabled={!hasPath}
              onClick={requestLook}
            >
              {ui.lookRecorded
                ? t("locationsPage.scene3d.trajectory.rerecordLook")
                : t("locationsPage.scene3d.trajectory.recordLook")}
            </button>
            <button
              type="button"
              role="menuitem"
              className={MENU_ITEM}
              onClick={() => {
                setMenuOpen(false)
                onLook({ mode: "observe", object_uid: observeUid })
              }}
            >
              <Crosshair className="h-3.5 w-3.5 shrink-0" />
              {t("locationsPage.scene3d.trajectory.lookObserve")}
              {ui.look.mode === "observe" ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
            </button>
            <button
              type="button"
              role="menuitem"
              className={cn(MENU_ITEM, "pl-8 text-ui-text-secondary")}
              onClick={() => {
                setMenuOpen(false)
                onLook({ mode: "observe", object_uid: observeUid })
                onPickObject()
              }}
            >
              {targetLabel && targetPrimitive ? (
                <EntityThumbSquare
                  name={targetLabel}
                  size="radio"
                  className="h-6 w-6"
                  fallbackIcon={<Cone className="h-3.5 w-3.5" />}
                />
              ) : targetLabel && targetThumb ? (
                <EntityThumbSquare name={targetLabel} size="radio" className="h-6 w-6" imageUrl={targetThumb} />
              ) : null}
              {targetLabel
                ? t("locationsPage.scene3d.trajectory.targetLine", { name: targetLabel })
                : t("locationsPage.scene3d.trajectory.pickTargetAction")}
            </button>
          </div>,
          portalRoot,
        )
      : null

  return (
    <div className="flex h-8 items-center gap-1 rounded-full border border-ui-border bg-ui-bg-elevated px-1 shadow-[var(--ui-shadow-md)]">
      <ActionHintTooltip label={t("locationsPage.scene3d.trajectory.drawHint")}>
        <Button
          type="button"
          size="sm"
          variant={ui.draw ? "default" : "ghost"}
          className="h-6 w-6 rounded-full p-0"
          aria-pressed={ui.draw}
          aria-label={t("locationsPage.scene3d.trajectory.drawHint")}
          onClick={() => onDraw(!ui.draw)}
        >
          <PenTool className="h-3.5 w-3.5" />
        </Button>
      </ActionHintTooltip>
      {DIVIDER}
      <ActionHintTooltip label={t("locationsPage.scene3d.trajectory.goToPathStartHint")}>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 w-6 rounded-full p-0"
          aria-label={t("locationsPage.scene3d.trajectory.goToPathStartHint")}
          disabled={!hasPath}
          onClick={onGoToPathStart}
        >
          <SkipBack className="h-3.5 w-3.5" />
        </Button>
      </ActionHintTooltip>
      {DIVIDER}
      <div
        ref={menuRootRef}
        className={cn(
          "relative inline-flex h-6 max-w-64 items-center rounded-full text-xs",
          ui.lookRecording ? "bg-ui-danger text-ui-on-accent" : "text-ui-text-primary hover:bg-ui-surface-raised",
        )}
      >
        <button
          ref={menuButtonRef}
          type="button"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          className={cn(
            "inline-flex h-6 items-center gap-1.5 rounded-full pl-2.5",
            modeDetail?.accent && !ui.lookRecording && !ui.observeArmed ? "pr-0" : "pr-2.5",
            LOCKED,
          )}
          disabled={playing && !ui.lookRecording}
          onClick={() => {
            if (ui.lookRecording) {
              setMenuOpen(false)
              onCancelLook()
              return
            }
            setMenuOpen((open) => !open)
          }}
        >
          {ui.lookRecording ? (
            <>
              <span className="h-2 w-2 shrink-0 rounded-full bg-current" />
              {t("locationsPage.scene3d.trajectory.lookRecordingLabel")}
            </>
          ) : ui.observeArmed ? (
            t("locationsPage.scene3d.trajectory.pickPrompt")
          ) : (
            <>
              {modeIcon}
              <span className="shrink-0">{modeTitle(ui, t)}</span>
              {modeDetail && !modeDetail.accent ? (
                <>
                  <span className="shrink-0">·</span>
                  <span className="max-w-40 truncate">{modeDetail.text}</span>
                </>
              ) : null}
            </>
          )}
        </button>
        {modeDetail?.accent && !ui.lookRecording && !ui.observeArmed ? (
          <button
            type="button"
            className={cn("inline-flex h-6 items-center gap-1.5 rounded-full pr-2.5 pl-1.5", LOCKED)}
            disabled={playing && !ui.lookRecording}
            onClick={() => {
              setMenuOpen(false)
              onPickObject()
            }}
          >
            <span className="shrink-0">·</span>
            <span className="max-w-40 truncate text-ui-accent">{modeDetail.text}</span>
          </button>
        ) : null}
        {menu}
      </div>
      <ConfirmActionDialog
        open={replaceOpen}
        onOpenChange={setReplaceOpen}
        message={t("locationsPage.scene3d.trajectory.replaceLook")}
        onConfirm={onArmLookRewrite}
      />
    </div>
  )
}

function modeTitle(ui: PathEditUi, t: TFunction<"workspace">) {
  if (ui.look.mode === "observe") return t("locationsPage.scene3d.trajectory.lookObserve")
  if (ui.look.mode === "recorded") return t("locationsPage.scene3d.trajectory.lookRecorded")
  return t("locationsPage.scene3d.trajectory.lookAlong")
}
